import React, { useState, useEffect } from 'react';
import "bootstrap-icons/font/bootstrap-icons.css";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import AppDataTable from '../../components/AppDataTable';
import { showConfirm } from '../../ConfirmBox';
import {
    fetchAnnouncements,
    addAnnouncement,
    updateAnnouncement,
    deleteAnnouncement
} from '../../apiCalls';

export default function Announcements() {
    const [announcements, setAnnouncements] = useState([]);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editData, setEditData] = useState(null);
    const [loading, setLoading] = useState(false);

    // ✅ 1. Added missing state for file
    const [selectedFile, setSelectedFile] = useState(null);

    const [formData, setFormData] = useState({
        heading: '',
        message: '',
        release_date: '',
        status: 'ACTIVE'
    });

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const loadData = async () => {
        try {
            const res = await fetchAnnouncements();
            setAnnouncements(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error("Error loading announcements", error);
            setAnnouncements([]);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddClick = () => {
        setEditData(null);
        setSelectedFile(null); // ✅ Reset file on add
        setFormData({ heading: '', message: '', release_date: '', status: 'ACTIVE' });
        setIsFormVisible(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        setSelectedFile(null); // ✅ Reset file on edit
        setFormData({
            heading: row.heading || '',
            message: row.message,
            release_date: row.release_date,
            status: row.status || 'ACTIVE'
        });
        setIsFormVisible(true);
    };

    const handleCloseForm = () => {
        setIsFormVisible(false);
        setEditData(null);
        setSelectedFile(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // ✅ 2. Use FormData instead of a simple object
            const submitData = new FormData();
            submitData.append('heading', formData.heading);
            submitData.append('message', formData.message);
            submitData.append('release_date', formData.release_date);
            submitData.append('status', formData.status);

            // Handle image logic similar to your Book example
            if (selectedFile) {
                submitData.append('image_file', selectedFile);
                submitData.append('image_filename', selectedFile.name);
            } else if (editData && editData.img) {
                submitData.append('image_filename', editData.img);
            }

            if (editData) {
                await updateAnnouncement(editData.id, submitData);
            } else {
                await addAnnouncement(submitData);
            }
            handleCloseForm();
            loadData();
        } catch (error) {
            console.error("Submit error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (row) => {
        showConfirm("Confirm Delete", "Are you sure?", async () => {
            try {
                await deleteAnnouncement(row.id);
                loadData();
            } catch (error) {
                console.error("Delete error:", error);
            }
        });
    };

    const columns = [
        { name: "Release Date", selector: row => row.release_date, sortable: true, width: "150px" },
        { name: "Heading", selector: row => row.heading, sortable: true, wrap: true, width: "200px" },
        { name: "Message", selector: row => row.message, sortable: true, wrap: true },
        {
            name: "Image",
            selector: row => row.img,
            cell: row => row.img ? <span className="text-success">{row.img}</span> : <span className="text-muted">No Image</span>
        },
        {
            name: "Action",
            width: "120px",
            cell: (row) => (
                <div className="d-flex gap-3 fs-5">
                    <i className="bi bi-pencil text-warning" style={{ cursor: "pointer" }} onClick={() => handleEdit(row)}></i>
                    <i className="bi bi-trash text-danger" style={{ cursor: "pointer" }} onClick={() => handleDelete(row)}></i>
                </div>
            ),
        }
    ];

    return (
        <>
            {isFormVisible ? (
                <>
                    <div className='page-title-container'>
                        <h2>{editData ? "Edit Announcement" : "Add Announcement"}</h2>
                        <button className="btn-close" onClick={handleCloseForm}></button>
                    </div>

                    <div className='card p-4 mt-4 border-0 shadow-sm bg-light'>
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Heading <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="heading"
                                        value={formData.heading}
                                        onChange={handleChange}
                                        placeholder="Enter announcement heading"
                                        required
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">Message <span className="text-danger">*</span></label>
                                    <textarea
                                        className="form-control"
                                        name="message"
                                        rows="3"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                    ></textarea>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">Release Date <span className="text-danger">*</span></label>
                                    <input type="date" className="form-control" name="release_date" value={formData.release_date} onChange={handleChange} required />
                                </div>

                                {/* ✅ 3. Added Image Input Field */}
                                <div className="col-md-12 mb-3">
                                    <label className="form-label">Announcement Image</label>
                                    <input type="file" className="form-control" accept="image/*" onChange={handleFileChange} />
                                    {editData?.img && !selectedFile && (
                                        <small className="text-muted d-block mt-1">Current: {editData.img}</small>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex gap-2 mt-3">
                                <button type="submit" className="btn btn-secondary btn-sm save-btn" disabled={loading}>
                                    {loading ? "Please wait..." : editData ? "Update" : "Save"}
                                </button>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCloseForm}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </>
            ) : (
                <>
                    <div className='page-title-container'>
                        <h2>Announcements <span>Manage the scrolling news ticker</span></h2>
                        <button className='btn btn-secondary btn-sm save-btn' onClick={handleAddClick}>Add Announcement</button>
                    </div>
                    <div className="table-responsive">
                        <AppDataTable columns={columns} data={announcements || []} />
                    </div>
                    <Tooltip id="editTooltip" /><Tooltip id="deleteTooltip" />
                </>
            )}
        </>
    );
}